import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { getEnvVar } from '../../../config/env';

interface PricingTier {
  id?: string;
  tier_name: string;
  tier_description?: string;
  
  // Subscription Pricing
  monthly_subscription_price?: number;
  monthly_subscription_price_usd?: number;
  included_credits_per_month?: number;
  credits_rollover_enabled?: boolean;
  display_currency?: string;
  trial_enabled?: boolean;
  trial_days?: number;
  
  // Feature Limits
  max_workspaces?: number;
  max_team_members?: number;
  max_contacts?: number;
  max_ai_employees?: number;
  max_whatsapp_instances?: number;
  max_voice_numbers?: number;
  storage_gb?: number;
  
  // Feature Flags
  email_support?: boolean;
  api_access_enabled?: boolean;
  white_label_enabled?: boolean;
  priority_support?: boolean;
  managed_service?: boolean;
  analytics_level?: string;
  integrations?: string[] | string; // JSONB array
  
  // Usage Pricing
  whatsapp_cost_per_message: number;
  voice_cost_per_minute: number;
  ai_task_cost_per_execution: number;
  email_cost_per_send: number;
  sms_cost_per_message: number;
  mms_cost_per_message: number;
  ai_usage_markup_percentage?: number;
  
  // Bulk Discount
  bulk_discount_threshold?: number;
  bulk_discount_percentage?: number;
  
  // Legacy fields (for backward compatibility)
  monthly_free_credits?: number;
  analytics_enabled?: boolean;
  
  // Call Pricing Configuration (stored separately but managed here)
  call_pricing_configs?: {
    human_to_human_outgoing_international?: { base_price_usd: number; markup_percentage: number };
    human_to_human_outgoing_indian?: { fixed_price_credits: number; markup_percentage: number };
    human_to_human_incoming_international?: { base_price_usd: number; markup_percentage: number };
    human_to_human_incoming_indian?: { fixed_price_credits: number; markup_percentage: number };
    ai_to_human_outgoing?: { base_price_usd: number; markup_percentage: number };
    human_to_ai_incoming?: { base_price_usd: number; markup_percentage: number };
  };
  
  // Status
  is_active: boolean;
  sort_order: number;
}

interface PricingTierModalProps {
  tier: PricingTier | null;
  onClose: () => void;
  onSave: () => void;
}

export const PricingTierModal: React.FC<PricingTierModalProps> = ({ tier, onClose, onSave }) => {
  const [formData, setFormData] = useState<PricingTier>({
    tier_name: '',
    tier_description: '',
    
    // Subscription Pricing
    monthly_subscription_price: 0,
    monthly_subscription_price_usd: 0,
    included_credits_per_month: 0,
    credits_rollover_enabled: true,
    display_currency: 'INR',
    trial_enabled: false,
    trial_days: 7,
    
    // Feature Limits
    max_workspaces: 1,
    max_team_members: 5,
    max_contacts: 1000,
    max_ai_employees: 5,
    max_whatsapp_instances: 3,
    max_voice_numbers: 2,
    storage_gb: 10,
    
    // Feature Flags
    email_support: false,
    api_access_enabled: false,
    white_label_enabled: false,
    priority_support: false,
    managed_service: false,
    analytics_level: 'basic',
    integrations: [],
    
    // Usage Pricing
    whatsapp_cost_per_message: 0.10,
    voice_cost_per_minute: 2.00,
    ai_task_cost_per_execution: 0.50,
    email_cost_per_send: 0.05,
    sms_cost_per_message: 0.15,
    mms_cost_per_message: 0.45,
    ai_usage_markup_percentage: 20.0,
    
    // Bulk Discount
    bulk_discount_threshold: 1000,
    bulk_discount_percentage: 10.00,
    
    // Legacy
    monthly_free_credits: 0,
    analytics_enabled: true,
    
    // Call Pricing Configuration
    call_pricing_configs: {
      human_to_human_outgoing_international: { base_price_usd: 0.015, markup_percentage: 20 },
      human_to_human_outgoing_indian: { fixed_price_credits: 2.0, markup_percentage: 20 },
      human_to_human_incoming_international: { base_price_usd: 0.010, markup_percentage: 20 },
      human_to_human_incoming_indian: { fixed_price_credits: 1.5, markup_percentage: 20 },
      ai_to_human_outgoing: { base_price_usd: 0.050, markup_percentage: 20 },
      human_to_ai_incoming: { base_price_usd: 0.050, markup_percentage: 20 },
    },
    
    // Status
    is_active: true,
    sort_order: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const API_BASE_URL = getEnvVar('VITE_FASTAPI_BASE_URL');

  useEffect(() => {
    const initializeTier = async () => {
      if (tier) {
        // Handle integrations if it's a string (JSON)
        const processedTier = { ...tier };
        if (typeof processedTier.integrations === 'string') {
          try {
            processedTier.integrations = JSON.parse(processedTier.integrations);
          } catch {
            processedTier.integrations = [];
          }
        }
        
        // Load call pricing configs if not present
        if (!processedTier.call_pricing_configs) {
          const loadedConfigs = await loadCallPricingConfigs();
          if (loadedConfigs) {
            processedTier.call_pricing_configs = loadedConfigs;
          }
        }
        
        const rawDays = Number(processedTier.trial_days);
        setFormData({
          ...processedTier,
          trial_enabled: Boolean(processedTier.trial_enabled),
          trial_days: Number.isFinite(rawDays) ? Math.min(30, Math.max(1, Math.round(rawDays))) : 7,
        });
      }
    };
    
    initializeTier();
  }, [tier]);

  const loadCallPricingConfigs = async () => {
    try {
      const token = localStorage.getItem('admin_auth_token');
      
      // Fetch all call pricing configs
      const configs = await Promise.all([
        // Human-to-Human Outgoing International
        fetch(`${API_BASE_URL}/api/v1/call-pricing/config?call_type=human_to_human&direction=outgoing&number_type=international&provider=telnyx`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(async r => {
          if (!r.ok) return null;
          const data = await r.json();
          return data;
        }).catch(() => null),
        // Human-to-Human Outgoing Indian
        fetch(`${API_BASE_URL}/api/v1/call-pricing/config?call_type=human_to_human&direction=outgoing&number_type=indian&provider=telnyx`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(async r => {
          if (!r.ok) return null;
          const data = await r.json();
          return data;
        }).catch(() => null),
        // Human-to-Human Incoming International
        fetch(`${API_BASE_URL}/api/v1/call-pricing/config?call_type=human_to_human&direction=incoming&number_type=international&provider=telnyx`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(async r => {
          if (!r.ok) return null;
          const data = await r.json();
          return data;
        }).catch(() => null),
        // Human-to-Human Incoming Indian
        fetch(`${API_BASE_URL}/api/v1/call-pricing/config?call_type=human_to_human&direction=incoming&number_type=indian&provider=telnyx`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(async r => {
          if (!r.ok) return null;
          const data = await r.json();
          return data;
        }).catch(() => null),
        // AI-to-Human Outgoing
        fetch(`${API_BASE_URL}/api/v1/call-pricing/config?call_type=ai_to_human&direction=outgoing&number_type=international&provider=retell`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(async r => {
          if (!r.ok) return null;
          const data = await r.json();
          return data;
        }).catch(() => null),
        // Human-to-AI Incoming
        fetch(`${API_BASE_URL}/api/v1/call-pricing/config?call_type=human_to_ai&direction=incoming&number_type=international&provider=retell`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(async r => {
          if (!r.ok) return null;
          const data = await r.json();
          return data;
        }).catch(() => null),
      ]);

      // The GET /config endpoint returns the config directly, not wrapped in data
      const callPricingConfigs = {
        human_to_human_outgoing_international: configs[0] ? {
          base_price_usd: configs[0].base_price_per_minute_usd,
          markup_percentage: configs[0].markup_percentage
        } : { base_price_usd: 0.015, markup_percentage: 20 },
        human_to_human_outgoing_indian: configs[1] ? {
          fixed_price_credits: configs[1].fixed_price_indian_credits,
          markup_percentage: configs[1].markup_percentage
        } : { fixed_price_credits: 2.0, markup_percentage: 20 },
        human_to_human_incoming_international: configs[2] ? {
          base_price_usd: configs[2].base_price_per_minute_usd,
          markup_percentage: configs[2].markup_percentage
        } : { base_price_usd: 0.010, markup_percentage: 20 },
        human_to_human_incoming_indian: configs[3] ? {
          fixed_price_credits: configs[3].fixed_price_indian_credits,
          markup_percentage: configs[3].markup_percentage
        } : { fixed_price_credits: 1.5, markup_percentage: 20 },
        ai_to_human_outgoing: configs[4] ? {
          base_price_usd: configs[4].base_price_per_minute_usd,
          markup_percentage: configs[4].markup_percentage
        } : { base_price_usd: 0.050, markup_percentage: 20 },
        human_to_ai_incoming: configs[5] ? {
          base_price_usd: configs[5].base_price_per_minute_usd,
          markup_percentage: configs[5].markup_percentage
        } : { base_price_usd: 0.050, markup_percentage: 20 },
      };

      return callPricingConfigs;
    } catch (error) {
      console.error('Error loading call pricing configs:', error);
    }
  };

  const saveCallPricingConfigs = async (token: string) => {
    if (!formData.call_pricing_configs) return;

    const configs = formData.call_pricing_configs;
    const updates = [];

    // Human-to-Human Outgoing International
    if (configs.human_to_human_outgoing_international) {
      updates.push(
        fetch(`${API_BASE_URL}/api/v1/admin/pricing/call-config`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            call_type: 'human_to_human',
            direction: 'outgoing',
            number_type: 'international',
            provider: 'telnyx',
            base_price_per_minute_usd: configs.human_to_human_outgoing_international.base_price_usd,
            markup_percentage: configs.human_to_human_outgoing_international.markup_percentage,
            is_active: true
          })
        })
      );
    }

    // Human-to-Human Outgoing Indian
    if (configs.human_to_human_outgoing_indian) {
      updates.push(
        fetch(`${API_BASE_URL}/api/v1/admin/pricing/call-config`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            call_type: 'human_to_human',
            direction: 'outgoing',
            number_type: 'indian',
            provider: 'telnyx',
            base_price_per_minute_usd: 0.010, // Default base
            fixed_price_indian_credits: configs.human_to_human_outgoing_indian.fixed_price_credits,
            markup_percentage: configs.human_to_human_outgoing_indian.markup_percentage,
            is_active: true
          })
        })
      );
    }

    // Human-to-Human Incoming International
    if (configs.human_to_human_incoming_international) {
      updates.push(
        fetch(`${API_BASE_URL}/api/v1/admin/pricing/call-config`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            call_type: 'human_to_human',
            direction: 'incoming',
            number_type: 'international',
            provider: 'telnyx',
            base_price_per_minute_usd: configs.human_to_human_incoming_international.base_price_usd,
            markup_percentage: configs.human_to_human_incoming_international.markup_percentage,
            is_active: true
          })
        })
      );
    }

    // Human-to-Human Incoming Indian
    if (configs.human_to_human_incoming_indian) {
      updates.push(
        fetch(`${API_BASE_URL}/api/v1/admin/pricing/call-config`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            call_type: 'human_to_human',
            direction: 'incoming',
            number_type: 'indian',
            provider: 'telnyx',
            base_price_per_minute_usd: 0.008, // Default base
            fixed_price_indian_credits: configs.human_to_human_incoming_indian.fixed_price_credits,
            markup_percentage: configs.human_to_human_incoming_indian.markup_percentage,
            is_active: true
          })
        })
      );
    }

    // AI-to-Human Outgoing
    if (configs.ai_to_human_outgoing) {
      updates.push(
        fetch(`${API_BASE_URL}/api/v1/admin/pricing/call-config`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            call_type: 'ai_to_human',
            direction: 'outgoing',
            number_type: 'international',
            provider: 'retell',
            base_price_per_minute_usd: configs.ai_to_human_outgoing.base_price_usd,
            markup_percentage: configs.ai_to_human_outgoing.markup_percentage,
            is_active: true
          })
        })
      );
    }

    // Human-to-AI Incoming
    if (configs.human_to_ai_incoming) {
      updates.push(
        fetch(`${API_BASE_URL}/api/v1/admin/pricing/call-config`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            call_type: 'human_to_ai',
            direction: 'incoming',
            number_type: 'international',
            provider: 'retell',
            base_price_per_minute_usd: configs.human_to_ai_incoming.base_price_usd,
            markup_percentage: configs.human_to_ai_incoming.markup_percentage,
            is_active: true
          })
        })
      );
    }

    // Execute all updates (don't fail if some fail)
    const results = await Promise.allSettled(updates);
    const errors = results
      .map((result, index) => {
        if (result.status === 'rejected') {
          return `Config ${index + 1} failed: ${result.reason}`;
        }
        return null;
      })
      .filter(Boolean);
    
    if (errors.length > 0) {
      console.warn('Some call pricing configs failed to save:', errors);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('admin_auth_token');
      const url = tier?.id
        ? `${API_BASE_URL}/api/v1/admin/pricing/tiers/${tier.id}`
        : `${API_BASE_URL}/api/v1/admin/pricing/tiers`;
      
      const method = tier?.id ? 'PUT' : 'POST';

      // Prepare data for API - ensure integrations is an array (not a JSON string)
      let integrationsArray = formData.integrations;
      if (typeof integrationsArray === 'string') {
        try {
          integrationsArray = JSON.parse(integrationsArray);
        } catch {
          integrationsArray = [];
        }
      }
      if (!Array.isArray(integrationsArray)) {
        integrationsArray = [];
      }
      
      const apiData = {
        ...formData,
        integrations: integrationsArray  // Send as array, backend expects List[str]
      };
      // When USD is 0 but INR > 0, send default USD = INR/92 so backend stores it
      if ((apiData.monthly_subscription_price_usd === 0 || apiData.monthly_subscription_price_usd == null) 
          && apiData.monthly_subscription_price > 0) {
        apiData.monthly_subscription_price_usd = Math.round((apiData.monthly_subscription_price / 92) * 100) / 100;
      }
      
      // Remove call_pricing_configs from tier data (it's stored separately)
      delete apiData.call_pricing_configs;
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save pricing tier');
      }

      // Save call pricing configs separately
      await saveCallPricingConfigs(token);

      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save pricing tier');
      console.error('Error saving pricing tier:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof PricingTier, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">
              {tier ? 'Edit Pricing Tier' : 'Create Pricing Tier'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-4">
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Info */}
              <div className="md:col-span-2">
                <h4 className="text-sm font-medium text-gray-900 mb-4">Basic Information</h4>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tier Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.tier_name}
                  onChange={(e) => handleChange('tier_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., professional, enterprise"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sort Order
                </label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => handleChange('sort_order', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.tier_description || ''}
                  onChange={(e) => handleChange('tier_description', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Tier description..."
                />
              </div>

              {/* Subscription Pricing */}
              <div className="md:col-span-2 mt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-4">Subscription Pricing</h4>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly Subscription Price (INR) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={formData.monthly_subscription_price || 0}
                  onChange={(e) => {
                    const inr = parseFloat(e.target.value) || 0;
                    handleChange('monthly_subscription_price', inr);
                    // Default USD = INR/92 when USD is empty (admin can change later)
                    const currentUsd = formData.monthly_subscription_price_usd ?? 0;
                    if (currentUsd === 0 && inr > 0) {
                      handleChange('monthly_subscription_price_usd', Math.round((inr / 92) * 100) / 100);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly Subscription Price (USD)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.monthly_subscription_price_usd ?? (formData.monthly_subscription_price ? Math.round((formData.monthly_subscription_price / 92) * 100) / 100 : 0)}
                  onChange={(e) => handleChange('monthly_subscription_price_usd', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Default: INR ÷ 92"
                />
                <p className="text-xs text-gray-500 mt-1">Direct USD price. Admin can override. Default: INR ÷ 92.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Included Credits per Month
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.included_credits_per_month || 0}
                  onChange={(e) => handleChange('included_credits_per_month', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Currency
                </label>
                <select
                  value={formData.display_currency || 'INR'}
                  onChange={(e) => handleChange('display_currency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.credits_rollover_enabled ?? true}
                    onChange={(e) => handleChange('credits_rollover_enabled', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Credits Rollover Enabled</span>
                </label>
              </div>

              <div className="md:col-span-2 mt-2">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Free trial</h4>
                <p className="text-xs text-gray-500 mb-3">
                  Off by default. When enabled, a workspace owner can start one trial of this plan. Days come from this catalog setting, not the customer.
                </p>
                <label className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    checked={formData.trial_enabled ?? false}
                    onChange={(e) => handleChange('trial_enabled', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Offer a free trial on this plan</span>
                </label>
                <div className="max-w-xs">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trial length (days)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={30}
                    disabled={!formData.trial_enabled}
                    value={formData.trial_days ?? 7}
                    onChange={(e) => {
                      const days = parseInt(e.target.value, 10);
                      handleChange('trial_days', Number.isFinite(days) ? Math.min(30, Math.max(1, days)) : 7);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  />
                </div>
              </div>

              {/* Feature Limits */}
              <div className="md:col-span-2 mt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-4">Feature Limits</h4>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Workspaces
                </label>
                <input
                  type="number"
                  min="-1"
                  value={formData.max_workspaces ?? 1}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    handleChange('max_workspaces', !isNaN(v) ? v : 1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="-1 = unlimited"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Team Members
                </label>
                <input
                  type="number"
                  min="-1"
                  value={formData.max_team_members ?? 5}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    handleChange('max_team_members', !isNaN(v) ? v : 5);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="-1 = unlimited"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Contacts
                </label>
                <input
                  type="number"
                  min="-1"
                  value={formData.max_contacts ?? 1000}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    handleChange('max_contacts', !isNaN(v) ? v : 1000);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="-1 = unlimited"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max AI Employees
                </label>
                <input
                  type="number"
                  min="-1"
                  value={formData.max_ai_employees ?? 5}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    handleChange('max_ai_employees', !isNaN(v) ? v : 5);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="-1 = unlimited"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max WhatsApp Instances
                </label>
                <input
                  type="number"
                  min="-1"
                  value={formData.max_whatsapp_instances ?? 3}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    handleChange('max_whatsapp_instances', !isNaN(v) ? v : 3);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="-1 = unlimited"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Voice Numbers
                </label>
                <input
                  type="number"
                  min="-1"
                  value={formData.max_voice_numbers ?? 2}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    handleChange('max_voice_numbers', !isNaN(v) ? v : 2);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="-1 = unlimited"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Storage (GB)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={formData.storage_gb || 10}
                  onChange={(e) => handleChange('storage_gb', parseFloat(e.target.value) || 10)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Usage Pricing */}
              <div className="md:col-span-2 mt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-4">Usage Pricing (Credits per Unit)</h4>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  WhatsApp (per message)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.whatsapp_cost_per_message}
                  onChange={(e) => handleChange('whatsapp_cost_per_message', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Voice (per minute)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.voice_cost_per_minute}
                  onChange={(e) => handleChange('voice_cost_per_minute', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  AI Task (per execution)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.ai_task_cost_per_execution}
                  onChange={(e) => handleChange('ai_task_cost_per_execution', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email (per send)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.email_cost_per_send}
                  onChange={(e) => handleChange('email_cost_per_send', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SMS (per message)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.sms_cost_per_message}
                  onChange={(e) => handleChange('sms_cost_per_message', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  MMS (per message)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.mms_cost_per_message}
                  onChange={(e) => handleChange('mms_cost_per_message', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  AI Usage Markup (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={formData.ai_usage_markup_percentage || 20}
                  onChange={(e) => handleChange('ai_usage_markup_percentage', parseFloat(e.target.value) || 20)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Percentage markup applied to actual AI costs</p>
              </div>

              {/* Bulk Discount */}
              <div className="md:col-span-2 mt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-4">Bulk Discount</h4>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Threshold
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.bulk_discount_threshold}
                  onChange={(e) => handleChange('bulk_discount_threshold', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Discount Percentage
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.bulk_discount_percentage}
                  onChange={(e) => handleChange('bulk_discount_percentage', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Feature Flags */}
              <div className="md:col-span-2 mt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-4">Feature Flags</h4>
              </div>

              <div className="md:col-span-2 grid grid-cols-2 gap-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.email_support ?? false}
                    onChange={(e) => handleChange('email_support', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Email Support</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.api_access_enabled ?? false}
                    onChange={(e) => handleChange('api_access_enabled', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">API Access</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.white_label_enabled ?? false}
                    onChange={(e) => handleChange('white_label_enabled', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">White Label</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.priority_support ?? false}
                    onChange={(e) => handleChange('priority_support', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Priority Support</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.managed_service ?? false}
                    onChange={(e) => handleChange('managed_service', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Managed Service</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.analytics_enabled ?? formData.analytics_level !== 'none'}
                    onChange={(e) => {
                      handleChange('analytics_enabled', e.target.checked);
                      if (!e.target.checked) handleChange('analytics_level', 'none');
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Analytics Enabled</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Analytics Level
                </label>
                <select
                  value={formData.analytics_level || 'basic'}
                  onChange={(e) => handleChange('analytics_level', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="none">None</option>
                  <option value="basic">Basic</option>
                  <option value="advanced">Advanced</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Integrations (comma-separated)
                </label>
                <input
                  type="text"
                  value={Array.isArray(formData.integrations) ? formData.integrations.join(', ') : (formData.integrations || '')}
                  onChange={(e) => {
                    const integrations = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                    handleChange('integrations', integrations);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., slack, zapier, hubspot"
                />
                <p className="text-xs text-gray-500 mt-1">Enter integration names separated by commas</p>
              </div>

              {/* Call Pricing Configuration */}
              <div className="md:col-span-2 mt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-4">Call Pricing Configuration</h4>
              </div>

              {/* Human-to-Human Outgoing - International */}
              <div className="md:col-span-2">
                <h5 className="text-xs font-semibold text-gray-700 mb-2">Human-to-Human Outgoing - International (Telnyx)</h5>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Base Price per Minute (USD)
                    </label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      value={formData.call_pricing_configs?.human_to_human_outgoing_international?.base_price_usd || 0.015}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        setFormData(prev => ({
                          ...prev,
                          call_pricing_configs: {
                            ...prev.call_pricing_configs,
                            human_to_human_outgoing_international: {
                              ...prev.call_pricing_configs?.human_to_human_outgoing_international,
                              base_price_usd: value,
                              markup_percentage: prev.call_pricing_configs?.human_to_human_outgoing_international?.markup_percentage || 20
                            }
                          }
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Markup Percentage (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={formData.call_pricing_configs?.human_to_human_outgoing_international?.markup_percentage || 20}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 20;
                        setFormData(prev => ({
                          ...prev,
                          call_pricing_configs: {
                            ...prev.call_pricing_configs,
                            human_to_human_outgoing_international: {
                              ...prev.call_pricing_configs?.human_to_human_outgoing_international,
                              base_price_usd: prev.call_pricing_configs?.human_to_human_outgoing_international?.base_price_usd || 0.015,
                              markup_percentage: value
                            }
                          }
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Human-to-Human Outgoing - Indian */}
              <div className="md:col-span-2">
                <h5 className="text-xs font-semibold text-gray-700 mb-2">Human-to-Human Outgoing - Indian (Telnyx)</h5>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Fixed Price per Minute (Credits)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.call_pricing_configs?.human_to_human_outgoing_indian?.fixed_price_credits || 2.0}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 2.0;
                        setFormData(prev => ({
                          ...prev,
                          call_pricing_configs: {
                            ...prev.call_pricing_configs,
                            human_to_human_outgoing_indian: {
                              ...prev.call_pricing_configs?.human_to_human_outgoing_indian,
                              fixed_price_credits: value,
                              markup_percentage: prev.call_pricing_configs?.human_to_human_outgoing_indian?.markup_percentage || 20
                            }
                          }
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Markup Percentage (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={formData.call_pricing_configs?.human_to_human_outgoing_indian?.markup_percentage || 20}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 20;
                        setFormData(prev => ({
                          ...prev,
                          call_pricing_configs: {
                            ...prev.call_pricing_configs,
                            human_to_human_outgoing_indian: {
                              ...prev.call_pricing_configs?.human_to_human_outgoing_indian,
                              fixed_price_credits: prev.call_pricing_configs?.human_to_human_outgoing_indian?.fixed_price_credits || 2.0,
                              markup_percentage: value
                            }
                          }
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Human-to-Human Incoming - International */}
              <div className="md:col-span-2">
                <h5 className="text-xs font-semibold text-gray-700 mb-2">Human-to-Human Incoming - International (Telnyx)</h5>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Base Price per Minute (USD)
                    </label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      value={formData.call_pricing_configs?.human_to_human_incoming_international?.base_price_usd || 0.010}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0.010;
                        setFormData(prev => ({
                          ...prev,
                          call_pricing_configs: {
                            ...prev.call_pricing_configs,
                            human_to_human_incoming_international: {
                              ...prev.call_pricing_configs?.human_to_human_incoming_international,
                              base_price_usd: value,
                              markup_percentage: prev.call_pricing_configs?.human_to_human_incoming_international?.markup_percentage || 20
                            }
                          }
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Markup Percentage (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={formData.call_pricing_configs?.human_to_human_incoming_international?.markup_percentage || 20}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 20;
                        setFormData(prev => ({
                          ...prev,
                          call_pricing_configs: {
                            ...prev.call_pricing_configs,
                            human_to_human_incoming_international: {
                              ...prev.call_pricing_configs?.human_to_human_incoming_international,
                              base_price_usd: prev.call_pricing_configs?.human_to_human_incoming_international?.base_price_usd || 0.010,
                              markup_percentage: value
                            }
                          }
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Human-to-Human Incoming - Indian */}
              <div className="md:col-span-2">
                <h5 className="text-xs font-semibold text-gray-700 mb-2">Human-to-Human Incoming - Indian (Telnyx)</h5>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Fixed Price per Minute (Credits)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.call_pricing_configs?.human_to_human_incoming_indian?.fixed_price_credits || 1.5}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 1.5;
                        setFormData(prev => ({
                          ...prev,
                          call_pricing_configs: {
                            ...prev.call_pricing_configs,
                            human_to_human_incoming_indian: {
                              ...prev.call_pricing_configs?.human_to_human_incoming_indian,
                              fixed_price_credits: value,
                              markup_percentage: prev.call_pricing_configs?.human_to_human_incoming_indian?.markup_percentage || 20
                            }
                          }
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Markup Percentage (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={formData.call_pricing_configs?.human_to_human_incoming_indian?.markup_percentage || 20}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 20;
                        setFormData(prev => ({
                          ...prev,
                          call_pricing_configs: {
                            ...prev.call_pricing_configs,
                            human_to_human_incoming_indian: {
                              ...prev.call_pricing_configs?.human_to_human_incoming_indian,
                              fixed_price_credits: prev.call_pricing_configs?.human_to_human_incoming_indian?.fixed_price_credits || 1.5,
                              markup_percentage: value
                            }
                          }
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* AI-to-Human Outgoing */}
              <div className="md:col-span-2">
                <h5 className="text-xs font-semibold text-gray-700 mb-2">AI-to-Human Outgoing (Retell)</h5>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Base Price per Minute (USD)
                    </label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      value={formData.call_pricing_configs?.ai_to_human_outgoing?.base_price_usd || 0.050}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0.050;
                        setFormData(prev => ({
                          ...prev,
                          call_pricing_configs: {
                            ...prev.call_pricing_configs,
                            ai_to_human_outgoing: {
                              ...prev.call_pricing_configs?.ai_to_human_outgoing,
                              base_price_usd: value,
                              markup_percentage: prev.call_pricing_configs?.ai_to_human_outgoing?.markup_percentage || 20
                            }
                          }
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Markup Percentage (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={formData.call_pricing_configs?.ai_to_human_outgoing?.markup_percentage || 20}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 20;
                        setFormData(prev => ({
                          ...prev,
                          call_pricing_configs: {
                            ...prev.call_pricing_configs,
                            ai_to_human_outgoing: {
                              ...prev.call_pricing_configs?.ai_to_human_outgoing,
                              base_price_usd: prev.call_pricing_configs?.ai_to_human_outgoing?.base_price_usd || 0.050,
                              markup_percentage: value
                            }
                          }
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Human-to-AI Incoming */}
              <div className="md:col-span-2">
                <h5 className="text-xs font-semibold text-gray-700 mb-2">Human-to-AI Incoming (Retell)</h5>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Base Price per Minute (USD)
                    </label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      value={formData.call_pricing_configs?.human_to_ai_incoming?.base_price_usd || 0.050}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0.050;
                        setFormData(prev => ({
                          ...prev,
                          call_pricing_configs: {
                            ...prev.call_pricing_configs,
                            human_to_ai_incoming: {
                              ...prev.call_pricing_configs?.human_to_ai_incoming,
                              base_price_usd: value,
                              markup_percentage: prev.call_pricing_configs?.human_to_ai_incoming?.markup_percentage || 20
                            }
                          }
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Markup Percentage (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={formData.call_pricing_configs?.human_to_ai_incoming?.markup_percentage || 20}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 20;
                        setFormData(prev => ({
                          ...prev,
                          call_pricing_configs: {
                            ...prev.call_pricing_configs,
                            human_to_ai_incoming: {
                              ...prev.call_pricing_configs?.human_to_ai_incoming,
                              base_price_usd: prev.call_pricing_configs?.human_to_ai_incoming?.base_price_usd || 0.050,
                              markup_percentage: value
                            }
                          }
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Saving...' : tier ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
